import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Loader2, Phone, MessageCircle, CalendarDays, Users, User, List,
  ChevronLeft, ChevronRight, PartyPopper, Plus, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { getEvents, updateEvent, createEventManual, deleteEvent, apiError } from "../lib/api";
import {
  TIME_SLOTS, EVENT_STATUSES, STATUS_LABELS, STATUS_STYLES, waLink, slotLabel,
} from "../lib/events";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";

function StatusBadge({ status, t }) {
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_STYLES[status] || ""}`}>
      {t(STATUS_LABELS[status] || status)}
    </span>
  );
}

export function EventsTab({ auth }) {
  const { t } = useLang();
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list"); // list | calendar
  const [fStatus, setFStatus] = useState("all");
  const [fDate, setFDate] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const params = { sort_by: sortBy };
      if (fStatus !== "all") params.status = fStatus;
      if (fDate) params.requested_date = fDate;
      setEvents(await getEvents(auth, params));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [auth, fStatus, fDate, sortBy]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteEvent(auth, toDelete.id);
      toast.success(t("eventDeleted"));
      setToDelete(null);
      if (selected?.id === toDelete.id) setSelected(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const list = events?.events || [];

  return (
    <div className="space-y-4" data-testid="admin-events">
      {/* View switch + Add */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto">
          <button
            data-testid="events-view-list"
            onClick={() => setView("list")}
            className={`inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold sm:w-auto ${view === "list" ? "bg-[#26422A] text-[#FFEEBC]" : "bg-white text-[#26422A] border-2 border-[#26422A]/30"}`}
          >
            <List className="h-4 w-4" /> {t("eventsList")}
          </button>
          <button
            data-testid="events-view-calendar"
            onClick={() => setView("calendar")}
            className={`inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold sm:w-auto ${view === "calendar" ? "bg-[#26422A] text-[#FFEEBC]" : "bg-white text-[#26422A] border-2 border-[#26422A]/30"}`}
          >
            <CalendarDays className="h-4 w-4" /> {t("eventsCalendar")}
          </button>
        </div>
        <Button
          data-testid="add-event-btn"
          onClick={() => setCreateOpen(true)}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#EA672D] px-4 text-sm font-bold text-white hover:bg-[#d0561f] sm:ml-auto sm:w-auto"
        >
          <Plus className="h-4 w-4" /> {t("addEvent")}
        </Button>
      </div>

      {view === "list" ? (
        <>
          {/* Filters */}
          <div className="flex flex-col gap-3 rounded-3xl border-2 border-[#26422A]/30 bg-white p-4 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="flex flex-col gap-1">
              <Label className="text-xs font-bold text-[#26422A]/70">{t("status")}</Label>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger data-testid="events-filter-status" className="h-11 w-full rounded-xl border-[#26422A]/40 font-semibold text-[#26422A] sm:w-[170px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="efilter-all">{t("allStatuses")}</SelectItem>
                  {EVENT_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`efilter-${s}`}>{t(STATUS_LABELS[s])}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs font-bold text-[#26422A]/70">{t("requestedDate")}</Label>
              <Input data-testid="events-filter-date" type="date" value={fDate}
                onChange={(e) => setFDate(e.target.value)}
                className="h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 font-semibold text-[#26422A] sm:w-[170px]" />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs font-bold text-[#26422A]/70">{t("sortBy")}</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="events-sort" className="h-11 w-full rounded-xl border-[#26422A]/40 font-semibold text-[#26422A] sm:w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at" data-testid="esort-created">{t("sortSubmission")}</SelectItem>
                  <SelectItem value="requested_date" data-testid="esort-requested">{t("sortRequestedDate")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(fStatus !== "all" || fDate) && (
              <Button data-testid="events-clear-filters" variant="outline"
                onClick={() => { setFStatus("all"); setFDate(""); }}
                className="h-11 w-full rounded-xl border-[#26422A]/40 font-bold text-[#26422A] sm:w-auto">
                {t("clearRange")}
              </Button>
            )}
          </div>

          {loading || !events ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#26422A]" /></div>
          ) : list.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-[#26422A]/30 bg-white py-14 text-center" data-testid="no-events">
              <PartyPopper className="mx-auto mb-3 h-10 w-10 text-[#26422A]/40" />
              <p className="font-bold text-[#26422A]">{t("noEvents")}</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="space-y-3 lg:hidden" data-testid="events-cards">
                <p className="px-1 text-xs font-bold text-[#26422A]/70">{events.count} {t("requests")}</p>
                {list.map((e, i) => (
                  <div key={e.id} data-testid={`event-card-${i}`}
                    className="relative w-full rounded-2xl border-2 border-[#26422A]/25 bg-white p-4 text-left">
                    <button
                      data-testid={`event-delete-${i}`}
                      onClick={(ev) => { ev.stopPropagation(); setToDelete(e); }}
                      aria-label={t("deleteEvent")}
                      className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-xl border-2 border-[#5D372A]/30 bg-white text-[#5D372A] hover:bg-[#5D372A]/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => setSelected(e)} className="block w-full text-left">
                    <div className="flex items-start justify-between gap-3 pr-10">
                      <div>
                        <div className="font-display text-lg font-bold leading-tight text-[#26422A]">{e.child_name} · {e.child_age}</div>
                        <div className="mt-0.5 text-sm text-[#26422A]/80">{e.parent_first_name} {e.parent_last_name}</div>
                      </div>
                      <StatusBadge status={e.status} t={t} />
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-[#26422A]">
                      <div className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-[#EA672D]" /> {e.requested_date} · {slotLabel(e.preferred_time, t)}</div>
                      <div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {e.est_children} {t("kids")} · {e.est_adults} {t("adults")}</div>
                      <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {e.phone_number}</div>
                    </div>
                    <div className="mt-2 text-sm font-bold text-[#26422A]">{e.price != null ? `₺${e.price}` : t("priceNotSet")}</div>
                    </button>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-3xl border-2 border-[#26422A]/30 bg-white lg:block" data-testid="events-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-[#26422A] text-[#FFEEBC]">
                      <tr>
                        <th className="px-3 py-3 font-bold">{t("parentName")}</th>
                        <th className="px-3 py-3 font-bold">{t("phone")}</th>
                        <th className="px-3 py-3 font-bold">{t("childName")}</th>
                        <th className="px-3 py-3 font-bold">{t("childAge")}</th>
                        <th className="px-3 py-3 font-bold">{t("requestedDate")}</th>
                        <th className="px-3 py-3 font-bold">{t("preferredTime")}</th>
                        <th className="px-3 py-3 font-bold">{t("kids")}</th>
                        <th className="px-3 py-3 font-bold">{t("adults")}</th>
                        <th className="px-3 py-3 font-bold">{t("priceTL")}</th>
                        <th className="px-3 py-3 font-bold">{t("status")}</th>
                        <th className="px-3 py-3 font-bold"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((e, i) => (
                        <motion.tr key={e.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                          onClick={() => setSelected(e)} data-testid={`event-row-${i}`}
                          className="cursor-pointer border-t border-[#26422A]/15 text-[#26422A] hover:bg-[#FFEEBC]/40">
                          <td className="px-3 py-3 font-bold">{e.parent_first_name} {e.parent_last_name}</td>
                          <td className="px-3 py-3">{e.phone_number}</td>
                          <td className="px-3 py-3">{e.child_name}</td>
                          <td className="px-3 py-3">{e.child_age}</td>
                          <td className="px-3 py-3">{e.requested_date}</td>
                          <td className="px-3 py-3 whitespace-nowrap">{slotLabel(e.preferred_time, t)}</td>
                          <td className="px-3 py-3">{e.est_children}</td>
                          <td className="px-3 py-3">{e.est_adults}</td>
                          <td className="px-3 py-3 font-bold">{e.price != null ? `₺${e.price}` : "--"}</td>
                          <td className="px-3 py-3"><StatusBadge status={e.status} t={t} /></td>
                          <td className="px-3 py-3">
                            <button
                              data-testid={`event-delete-row-${i}`}
                              onClick={(ev) => { ev.stopPropagation(); setToDelete(e); }}
                              aria-label={t("deleteEvent")}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border-2 border-[#5D372A]/30 bg-white text-[#5D372A] hover:bg-[#5D372A]/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <EventsCalendar events={list} onSelect={setSelected} t={t} loading={loading || !events} />
      )}

      <EventDetailDialog
        event={selected}
        auth={auth}
        t={t}
        onClose={() => setSelected(null)}
        onSaved={(updated) => { setSelected(null); load(); }}
        onDelete={(ev) => setToDelete(ev)}
      />
      <CreateEventDialog
        open={createOpen}
        auth={auth}
        t={t}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => { if (!v && !deleting) setToDelete(null); }}>
        <AlertDialogContent data-testid="event-delete-dialog" className="rounded-3xl border-2 border-[#26422A]/40 bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-[#26422A]">{t("deleteEvent")}</AlertDialogTitle>
            <AlertDialogDescription className="text-[#26422A]/80">
              {toDelete ? `${toDelete.child_name} · ${toDelete.requested_date}` : ""}
              <span className="mt-2 block">{t("deleteEventConfirm")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="event-delete-cancel" disabled={deleting}
              className="rounded-xl border-2 border-[#26422A]/40 font-bold text-[#26422A]">
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="event-delete-confirm"
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="rounded-xl bg-[#5D372A] font-bold text-white hover:bg-[#49291f]"
            >
              {deleting ? <Loader2 className="h-5 w-5 animate-spin" /> : t("deleteEvent")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EventsCalendar({ events, onSelect, t, loading }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const confirmed = events.filter((e) => e.status === "confirmed" && e.agreed_date);
  const byDate = {};
  confirmed.forEach((e) => { (byDate[e.agreed_date] = byDate[e.agreed_date] || []).push(e); });

  const first = new Date(cursor.y, cursor.m, 1);
  const startDow = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthKeys = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
  const dow = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const prev = () => setCursor((c) => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 });
  const next = () => setCursor((c) => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 });
  const key = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#26422A]" /></div>;

  return (
    <div className="rounded-3xl border-2 border-[#26422A]/30 bg-white p-4 sm:p-5" data-testid="events-calendar">
      <div className="mb-4 flex items-center justify-between">
        <button data-testid="cal-prev" onClick={prev} className="rounded-xl border-2 border-[#26422A]/30 p-2 text-[#26422A] hover:bg-[#FFEEBC]/40"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-display text-lg font-bold text-[#26422A]">{t(monthKeys[cursor.m])} {cursor.y}</div>
        <button data-testid="cal-next" onClick={next} className="rounded-xl border-2 border-[#26422A]/30 p-2 text-[#26422A] hover:bg-[#FFEEBC]/40"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <p className="mb-3 text-xs font-bold text-[#26422A]/60">{t("calendarHint")}</p>
      <div className="grid grid-cols-7 gap-1 text-center">
        {dow.map((d) => <div key={d} className="py-1 text-xs font-bold text-[#26422A]/60">{t(d)}</div>)}
        {cells.map((d, i) => (
          <div key={i} className={`min-h-[64px] rounded-lg p-1 text-left ${d ? "border border-[#26422A]/10 bg-[#FFFDF4]" : ""}`}>
            {d && <div className="text-xs font-bold text-[#26422A]/70">{d}</div>}
            {d && (byDate[key(d)] || []).map((e) => (
              <button key={e.id} data-testid={`cal-event-${e.id}`} onClick={() => onSelect(e)}
                className="mt-0.5 block w-full truncate rounded bg-[#26422A] px-1 py-0.5 text-left text-[10px] font-bold text-[#FFEEBC] hover:bg-[#1B3320]">
                {e.agreed_time ? e.agreed_time.split(" ")[0] : ""} {e.child_name}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function EventDetailDialog({ event, auth, t, onClose, onSaved, onDelete }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setForm({
        status: event.status || "new",
        price: event.price ?? "",
        preferred_time: event.preferred_time || "",
        alt_time: event.alt_time || "",
        agreed_date: event.agreed_date || "",
        agreed_time: event.agreed_time || "",
        est_children: event.est_children || "",
        est_adults: event.est_adults || "",
        deposit_received: !!event.deposit_received,
        deposit_amount: event.deposit_amount ?? "",
        deposit_date: event.deposit_date || "",
        manager_notes: event.manager_notes || "",
      });
    }
  }, [event]);

  if (!event) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        status: form.status,
        price: form.price === "" ? null : Number(form.price),
        preferred_time: form.preferred_time || null,
        alt_time: form.alt_time || null,
        agreed_date: form.agreed_date || null,
        agreed_time: form.agreed_time || null,
        est_children: String(form.est_children),
        est_adults: String(form.est_adults),
        deposit_received: form.deposit_received,
        deposit_amount: form.deposit_amount === "" ? null : Number(form.deposit_amount),
        deposit_date: form.deposit_date || null,
        manager_notes: form.manager_notes || null,
      };
      await updateEvent(auth, event.id, patch);
      toast.success(t("savedSuccessfully"));
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const fieldCls = "h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 font-semibold text-[#26422A]";

  return (
    <Dialog open={!!event} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent data-testid="event-detail-dialog" className="max-h-[90vh] overflow-y-auto rounded-3xl border-2 border-[#26422A]/40 bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#26422A]">
            {event.child_name} · {t("turningAge")} {event.child_age}
          </DialogTitle>
        </DialogHeader>

        {/* Submitted info (read-only) */}
        <div className="rounded-2xl bg-[#FFFDF4] p-4 text-sm text-[#26422A]">
          <div className="mb-2 flex items-center gap-2 font-bold"><User className="h-4 w-4" /> {event.parent_first_name} {event.parent_last_name}</div>
          <div className="grid grid-cols-2 gap-y-1">
            <span className="text-[#26422A]/60">{t("requestedDate")}</span><span className="font-semibold text-right">{event.requested_date}</span>
            <span className="text-[#26422A]/60">{t("submittedOn")}</span><span className="font-semibold text-right">{(event.created_at || "").slice(0, 10)}</span>
          </div>
          {event.special_requests && <div className="mt-2 border-t border-[#26422A]/10 pt-2"><span className="text-[#26422A]/60">{t("specialRequests")}: </span>{event.special_requests}</div>}
          <div className="mt-3 flex gap-2">
            <a data-testid="event-call-link" href={`tel:${event.phone_number}`} className="inline-flex items-center gap-1.5 rounded-xl bg-[#26422A] px-3 py-2 text-xs font-bold text-[#FFEEBC]"><Phone className="h-3.5 w-3.5" /> {event.phone_number}</a>
            <a data-testid="event-whatsapp-link" href={waLink(event.phone_number)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-xl bg-[#2F6B4F] px-3 py-2 text-xs font-bold text-white"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
          </div>
        </div>

        {/* Manager-editable */}
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-bold text-[#26422A]">{t("status")}</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="event-status-select" className={fieldCls + " mt-1"}><SelectValue /></SelectTrigger>
              <SelectContent>
                {EVENT_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`estatus-${s}`}>{t(STATUS_LABELS[s])}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-bold text-[#26422A]">{t("priceTL")}</Label>
            <Input data-testid="event-price-input" type="number" min="0" value={form.price}
              placeholder={t("priceNotSet")} onChange={(e) => set("price", e.target.value)} className={fieldCls + " mt-1"} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-sm font-bold text-[#26422A]">{t("preferredTime")}</Label>
              <Select value={form.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
                <SelectTrigger data-testid="event-preferred-time" className={fieldCls + " mt-1"}><SelectValue placeholder={t("selectTime")} /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value}>{slotLabel(s.value, t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold text-[#26422A]">{t("altTime")}</Label>
              <Select value={form.alt_time} onValueChange={(v) => set("alt_time", v)}>
                <SelectTrigger data-testid="event-alt-time" className={fieldCls + " mt-1"}><SelectValue placeholder="--" /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value}>{slotLabel(s.value, t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-sm font-bold text-[#26422A]">{t("agreedDate")}</Label>
              <Input data-testid="event-agreed-date" type="date" value={form.agreed_date}
                onChange={(e) => set("agreed_date", e.target.value)} className={fieldCls + " mt-1"} />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#26422A]">{t("agreedTime")}</Label>
              <Select value={form.agreed_time} onValueChange={(v) => set("agreed_time", v)}>
                <SelectTrigger data-testid="event-agreed-time" className={fieldCls + " mt-1"}><SelectValue placeholder="--" /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value}>{slotLabel(s.value, t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-sm font-bold text-[#26422A]">{t("estChildren")}</Label>
              <Input data-testid="event-children-input" type="number" min="0" value={form.est_children}
                onChange={(e) => set("est_children", e.target.value)} className={fieldCls + " mt-1"} />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#26422A]">{t("estAdults")}</Label>
              <Input data-testid="event-adults-input" type="number" min="0" value={form.est_adults}
                onChange={(e) => set("est_adults", e.target.value)} className={fieldCls + " mt-1"} />
            </div>
          </div>

          <div className="rounded-2xl border-2 border-[#26422A]/20 p-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-bold text-[#26422A]">{t("depositReceived")}</Label>
              <Switch data-testid="event-deposit-switch" checked={form.deposit_received} onCheckedChange={(v) => set("deposit_received", v)} />
            </div>
            {form.deposit_received && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input data-testid="event-deposit-amount" type="number" min="0" placeholder={t("depositAmount")}
                  value={form.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} className={fieldCls} />
                <Input data-testid="event-deposit-date" type="date" value={form.deposit_date}
                  onChange={(e) => set("deposit_date", e.target.value)} className={fieldCls} />
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm font-bold text-[#26422A]">{t("internalNotes")}</Label>
            <Textarea data-testid="event-notes-input" rows={4} value={form.manager_notes}
              onChange={(e) => set("manager_notes", e.target.value)} className="mt-1 rounded-xl border-[#26422A]/40 font-medium text-[#26422A]" />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            data-testid="event-detail-delete-btn"
            variant="outline"
            onClick={() => { onClose(); onDelete && onDelete(event); }}
            disabled={saving}
            className="h-12 w-full gap-2 rounded-xl border-2 border-[#5D372A]/40 font-bold text-[#5D372A] hover:bg-[#5D372A]/10 sm:w-auto"
          >
            <Trash2 className="h-4 w-4" /> {t("deleteEvent")}
          </Button>
          <Button data-testid="event-save-btn" onClick={save} disabled={saving}
            className="h-12 w-full rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateEventDialog({ open, auth, t, onClose, onCreated }) {
  const empty = {
    parent_first_name: "", parent_last_name: "", phone_number: "",
    child_name: "", child_age: "", requested_date: "", preferred_time: "",
    alt_time: "", est_children: "", est_adults: "", price: "", status: "new", manager_notes: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fieldCls = "h-11 w-full min-w-0 rounded-xl border-[#26422A]/40 font-semibold text-[#26422A] mt-1";

  const save = async () => {
    const required = ["parent_first_name", "parent_last_name", "phone_number",
      "child_name", "child_age", "requested_date", "preferred_time", "est_children", "est_adults"];
    for (const k of required) if (!String(form[k]).trim()) return toast.error(t("fillRequiredFields"));
    if (form.phone_number.replace(/\D/g, "").length < 7) return toast.error(t("pleaseEnterValidPhone"));
    setSaving(true);
    try {
      await createEventManual(auth, {
        parent_first_name: form.parent_first_name.trim(),
        parent_last_name: form.parent_last_name.trim(),
        phone_number: form.phone_number.trim(),
        child_name: form.child_name.trim(),
        child_age: String(form.child_age),
        requested_date: form.requested_date,
        preferred_time: form.preferred_time,
        alt_time: form.alt_time || null,
        est_children: String(form.est_children),
        est_adults: String(form.est_adults),
        price: form.price === "" ? null : Number(form.price),
        status: form.status,
        manager_notes: form.manager_notes || null,
      });
      toast.success(t("savedSuccessfully"));
      setForm(empty);
      onCreated();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) { setForm(empty); onClose(); } }}>
      <DialogContent data-testid="create-event-dialog" className="max-h-[90vh] overflow-y-auto rounded-3xl border-2 border-[#26422A]/40 bg-white">
        <DialogHeader><DialogTitle className="font-display text-xl text-[#26422A]">{t("addEvent")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input data-testid="ce-first-name" placeholder={t("firstName")} value={form.parent_first_name} onChange={(e) => set("parent_first_name", e.target.value)} className={fieldCls} />
            <Input data-testid="ce-last-name" placeholder={t("lastName")} value={form.parent_last_name} onChange={(e) => set("parent_last_name", e.target.value)} className={fieldCls} />
          </div>
          <Input data-testid="ce-phone" type="tel" placeholder={t("phone")} value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)} className={fieldCls} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input data-testid="ce-child-name" placeholder={t("childName")} value={form.child_name} onChange={(e) => set("child_name", e.target.value)} className={fieldCls} />
            <Input data-testid="ce-child-age" type="number" min="0" placeholder={t("childAge")} value={form.child_age} onChange={(e) => set("child_age", e.target.value)} className={fieldCls} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-bold text-[#26422A]/70">{t("requestedDate")}</Label>
              <Input data-testid="ce-date" type="date" value={form.requested_date} onChange={(e) => set("requested_date", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#26422A]/70">{t("preferredTime")}</Label>
              <Select value={form.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
                <SelectTrigger data-testid="ce-time" className={fieldCls}><SelectValue placeholder={t("selectTime")} /></SelectTrigger>
                <SelectContent>{TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`ce-time-${s.value}`}>{slotLabel(s.value, t)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-bold text-[#26422A]/70">{t("altTime")}</Label>
              <Select value={form.alt_time} onValueChange={(v) => set("alt_time", v)}>
                <SelectTrigger data-testid="ce-alt-time" className={fieldCls}><SelectValue placeholder="--" /></SelectTrigger>
                <SelectContent>{TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value}>{slotLabel(s.value, t)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold text-[#26422A]/70">{t("status")}</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="ce-status" className={fieldCls}><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`ce-status-${s}`}>{t(STATUS_LABELS[s])}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs font-bold text-[#26422A]/70">{t("kids")}</Label>
              <Input data-testid="ce-children" type="number" min="0" value={form.est_children} onChange={(e) => set("est_children", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#26422A]/70">{t("adults")}</Label>
              <Input data-testid="ce-adults" type="number" min="0" value={form.est_adults} onChange={(e) => set("est_adults", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#26422A]/70">{t("priceTL")}</Label>
              <Input data-testid="ce-price" type="number" min="0" placeholder={t("priceNotSet")} value={form.price} onChange={(e) => set("price", e.target.value)} className={fieldCls} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#26422A]/70">{t("internalNotes")}</Label>
            <Textarea data-testid="ce-notes" rows={3} value={form.manager_notes} onChange={(e) => set("manager_notes", e.target.value)} className="mt-1 rounded-xl border-[#26422A]/40 font-medium text-[#26422A]" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="ce-save-btn" onClick={save} disabled={saving} className="h-12 w-full rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("createEvent")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

